import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import styles from './LocatiesBeheer.module.css';

export default function LocatiesBeheer() {
  const { heeftPermissie } = useAuth();
  const isSuperadmin = heeftPermissie('superadmin.alles');

  const [organisaties, setOrganisaties]   = useState([]);
  const [geselecteerdeOrg, setGeselecteerdeOrg] = useState('');
  const [locaties, setLocaties]           = useState([]);
  const [geselecteerdeLocatie, setGeselecteerdeLocatie] = useState(null);
  const [afdelingen, setAfdelingen]       = useState([]);
  const [laden, setLaden]                 = useState(false);

  const [toonLocatieModal, setToonLocatieModal]   = useState(false);
  const [toonAfdelingModal, setToonAfdelingModal] = useState(false);
  const [bewerkLocatie, setBewerkLocatie]         = useState(null);
  const [bewerkAfdeling, setBewerkAfdeling]       = useState(null);
  const [locatieNaam, setLocatieNaam]             = useState('');
  const [afdelingNaam, setAfdelingNaam]           = useState('');

  useEffect(() => {
    if (isSuperadmin) {
      axios.get('/api/organisaties')
        .then(r => setOrganisaties(r.data.organisaties))
        .catch(() => {});
    } else {
      laadLocaties();
    }
  }, [isSuperadmin]);

  useEffect(() => {
    if (isSuperadmin && geselecteerdeOrg) laadLocaties();
  }, [geselecteerdeOrg]);

  const laadLocaties = useCallback(async () => {
    setLaden(true);
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      const res = await axios.get('/api/locaties', { params });
      setLocaties(res.data.locaties);
      setGeselecteerdeLocatie(null);
      setAfdelingen([]);
    } catch {
      toast.error('Locaties laden mislukt.');
    } finally {
      setLaden(false);
    }
  }, [geselecteerdeOrg]);

  async function laadAfdelingen(locatie) {
    setGeselecteerdeLocatie(locatie);
    try {
      const res = await axios.get('/api/locaties/afdelingen', { params: { locatie_id: locatie.id } });
      setAfdelingen(res.data.afdelingen);
    } catch {
      toast.error('Afdelingen laden mislukt.');
    }
  }

  // Locatie opslaan
  async function handleLocatieOpslaan(e) {
    e.preventDefault();
    if (!locatieNaam.trim()) return toast.error('Naam is verplicht.');
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      if (bewerkLocatie) {
        await axios.put(`/api/locaties/${bewerkLocatie.id}`, { naam: locatieNaam }, { params });
        toast.success('Locatie bijgewerkt.');
      } else {
        await axios.post('/api/locaties', { naam: locatieNaam, organisatie_id: geselecteerdeOrg || undefined }, { params });
        toast.success('Locatie aangemaakt.');
      }
      setToonLocatieModal(false);
      setLocatieNaam('');
      setBewerkLocatie(null);
      laadLocaties();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt.');
    }
  }

  async function handleLocatieVerwijderen(id) {
    if (!window.confirm('Weet u zeker dat u deze locatie wilt verwijderen?')) return;
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      await axios.delete(`/api/locaties/${id}`, { params });
      toast.success('Locatie verwijderd.');
      if (geselecteerdeLocatie?.id === id) { setGeselecteerdeLocatie(null); setAfdelingen([]); }
      laadLocaties();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt.');
    }
  }

  // Afdeling opslaan
  async function handleAfdelingOpslaan(e) {
    e.preventDefault();
    if (!afdelingNaam.trim()) return toast.error('Naam is verplicht.');
    try {
      const params = geselecteerdeOrg ? { organisatie_id: geselecteerdeOrg } : {};
      if (bewerkAfdeling) {
        await axios.put(`/api/locaties/afdelingen/${bewerkAfdeling.id}`, { naam: afdelingNaam }, { params });
        toast.success('Afdeling bijgewerkt.');
      } else {
        await axios.post('/api/locaties/afdelingen', {
          locatie_id: geselecteerdeLocatie.id,
          naam: afdelingNaam,
          organisatie_id: geselecteerdeOrg || undefined,
        }, { params });
        toast.success('Afdeling aangemaakt.');
      }
      setToonAfdelingModal(false);
      setAfdelingNaam('');
      setBewerkAfdeling(null);
      laadAfdelingen(geselecteerdeLocatie);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt.');
    }
  }

  async function handleAfdelingVerwijderen(id) {
    if (!window.confirm('Weet u zeker dat u deze afdeling wilt verwijderen?')) return;
    try {
      await axios.delete(`/api/locaties/afdelingen/${id}`);
      toast.success('Afdeling verwijderd.');
      laadAfdelingen(geselecteerdeLocatie);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt.');
    }
  }

  // Superadmin zonder org keuze
  if (isSuperadmin && !geselecteerdeOrg) {
    return (
      <div className={styles.pagina}>
        <h1 className={styles.titel}>Locaties & Afdelingen</h1>
        <p className={styles.subtitel}>Kies een organisatie</p>
        <div className={styles.orgKeuze}>
          {organisaties.map(org => (
            <button key={org.id} className={styles.orgKnop}
              onClick={() => setGeselecteerdeOrg(org.id.toString())}>
              <div className={styles.orgBolletje} style={{ background: org.primaire_kleur || '#1B4F8A' }} />
              <span className={styles.orgNaam}>{org.naam}</span>
              <span className={styles.orgPijl}>→</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const orgNaam = organisaties.find(o => o.id.toString() === geselecteerdeOrg)?.naam;

  return (
    <div className={styles.pagina}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.titel}>Locaties & Afdelingen</h1>
          <p className={styles.subtitel}>{orgNaam || 'Uw organisatie'}</p>
        </div>
        <div className={styles.headerKnoppen}>
          {isSuperadmin && (
            <button className={styles.knopSecundair} onClick={() => { setGeselecteerdeOrg(''); setLocaties([]); }}>
              ← Andere organisatie
            </button>
          )}
          <button className={styles.knopPrimair} onClick={() => { setBewerkLocatie(null); setLocatieNaam(''); setToonLocatieModal(true); }}>
            + Locatie toevoegen
          </button>
        </div>
      </div>

      <div className={styles.layout}>
        {/* Locaties kolom */}
        <div className={styles.kolom}>
          <h2 className={styles.kolomTitel}>Locaties</h2>
          {laden ? (
            <div className={styles.laadTekst}>Laden...</div>
          ) : locaties.length === 0 ? (
            <div className={styles.leeg}>Nog geen locaties aangemaakt.</div>
          ) : (
            locaties.map(locatie => (
              <div key={locatie.id}
                className={`${styles.locatieKaart} ${geselecteerdeLocatie?.id === locatie.id ? styles.geselecteerd : ''}`}
                onClick={() => laadAfdelingen(locatie)}>
                <div className={styles.locatieInfo}>
                  <span className={styles.locatieNaam}>{locatie.naam}</span>
                  <span className={styles.locatieMeta}>
                    {locatie.aantal_afdelingen} afdeling(en)
                    {!locatie.actief && <span className={styles.inactiefBadge}>Inactief</span>}
                  </span>
                </div>
                <div className={styles.locatieActies}>
                  <button className={styles.knopKlein} onClick={e => {
                    e.stopPropagation();
                    setBewerkLocatie(locatie);
                    setLocatieNaam(locatie.naam);
                    setToonLocatieModal(true);
                  }}>✏️</button>
                  <button className={`${styles.knopKlein} ${styles.rood}`} onClick={e => {
                    e.stopPropagation();
                    handleLocatieVerwijderen(locatie.id);
                  }}>🗑</button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Afdelingen kolom */}
        <div className={styles.kolom}>
          <div className={styles.kolomHeader}>
            <h2 className={styles.kolomTitel}>
              {geselecteerdeLocatie ? `Afdelingen — ${geselecteerdeLocatie.naam}` : 'Afdelingen'}
            </h2>
            {geselecteerdeLocatie && (
              <button className={styles.knopPrimair} onClick={() => {
                setBewerkAfdeling(null);
                setAfdelingNaam('');
                setToonAfdelingModal(true);
              }}>
                + Afdeling toevoegen
              </button>
            )}
          </div>

          {!geselecteerdeLocatie ? (
            <div className={styles.leeg}>Klik op een locatie om afdelingen te bekijken.</div>
          ) : afdelingen.length === 0 ? (
            <div className={styles.leeg}>Nog geen afdelingen voor deze locatie.</div>
          ) : (
            afdelingen.map(afd => (
              <div key={afd.id} className={styles.afdelingRij}>
                <span className={styles.afdelingNaam}>
                  {afd.naam}
                  {!afd.actief && <span className={styles.inactiefBadge}>Inactief</span>}
                </span>
                <div className={styles.locatieActies}>
                  <button className={styles.knopKlein} onClick={() => {
                    setBewerkAfdeling(afd);
                    setAfdelingNaam(afd.naam);
                    setToonAfdelingModal(true);
                  }}>✏️</button>
                  <button className={`${styles.knopKlein} ${styles.rood}`}
                    onClick={() => handleAfdelingVerwijderen(afd.id)}>🗑</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal: locatie */}
      {toonLocatieModal && (
        <div className={styles.overlay} onClick={() => setToonLocatieModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>{bewerkLocatie ? 'Locatie bewerken' : 'Locatie toevoegen'}</h2>
            <form onSubmit={handleLocatieOpslaan} className={styles.form}>
              <div className={styles.veld}>
                <label>Naam *</label>
                <input type="text" value={locatieNaam} onChange={e => setLocatieNaam(e.target.value)}
                  placeholder="bijv. Hoofdlocatie Amsterdam" autoFocus required />
              </div>
              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair} onClick={() => setToonLocatieModal(false)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair}>Opslaan</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: afdeling */}
      {toonAfdelingModal && (
        <div className={styles.overlay} onClick={() => setToonAfdelingModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitel}>{bewerkAfdeling ? 'Afdeling bewerken' : `Afdeling toevoegen — ${geselecteerdeLocatie?.naam}`}</h2>
            <form onSubmit={handleAfdelingOpslaan} className={styles.form}>
              <div className={styles.veld}>
                <label>Naam *</label>
                <input type="text" value={afdelingNaam} onChange={e => setAfdelingNaam(e.target.value)}
                  placeholder="bijv. Afdeling PG" autoFocus required />
              </div>
              <div className={styles.modalKnoppen}>
                <button type="button" className={styles.knopSecundair} onClick={() => setToonAfdelingModal(false)}>Annuleren</button>
                <button type="submit" className={styles.knopPrimair}>Opslaan</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
